import {
  aggregateBandwidthReport,
  classifyTransferUrl,
  formatBandwidthSummary,
} from "@/diagnostics/networkBandwidth/bandwidthAggregator";
import type {
  BandwidthMonitorConfig,
  BandwidthMonitorGlobal,
  BandwidthReport,
  NetworkTransfer,
  TransferDirection,
} from "@/diagnostics/networkBandwidth/types";
import { DEFAULT_BANDWIDTH_MONITOR_CONFIG } from "@/diagnostics/networkBandwidth/types";

let transferCounter = 0;
let activeMonitorInstances = 0;
let scenarioNavigationDepth = 0;

const nextTransferId = (): string => {
  transferCounter += 1;
  return `xfer-${transferCounter}`;
};

const bodyByteLength = (body: BodyInit | null | undefined): number => {
  if (body === null || body === undefined) return 0;
  if (typeof body === "string") return new TextEncoder().encode(body).length;
  if (body instanceof ArrayBuffer) return body.byteLength;
  if (ArrayBuffer.isView(body)) return body.byteLength;
  if (body instanceof Blob) return body.size;
  return 0;
};

const readResponseBytes = async (
  response: Response,
  maxBodyMeasureBytes: number,
): Promise<number> => {
  const contentLength = response.headers.get("content-length");
  if (contentLength) {
    const parsed = Number.parseInt(contentLength, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  try {
    const clone = response.clone();
    const buffer = await clone.arrayBuffer();
    if (buffer.byteLength > maxBodyMeasureBytes) {
      return maxBodyMeasureBytes;
    }
    return buffer.byteLength;
  } catch {
    return 0;
  }
};

const sanitizeUrl = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split("?")[0] ?? url;
  }
};

const getConnectionInfo = (): {
  downlinkMbps: number | null;
  rttMs: number | null;
  effectiveType: string | null;
} => {
  const nav = navigator as Navigator & {
    connection?: {
      downlink?: number;
      rtt?: number;
      effectiveType?: string;
    };
  };
  const conn = nav.connection;
  if (!conn) {
    return { downlinkMbps: null, rttMs: null, effectiveType: null };
  }
  return {
    downlinkMbps: conn.downlink ?? null,
    rttMs: conn.rtt ?? null,
    effectiveType: conn.effectiveType ?? null,
  };
};

/** In-browser network bandwidth monitor (active only while diagnostics page is open). */
export class NetworkBandwidthMonitor {
  private readonly config: BandwidthMonitorConfig;
  private sessionStartMs: number;
  private transfers: NetworkTransfer[] = [];
  private scenarioMarks: Array<{
    name: string;
    atMs: number;
    report: BandwidthReport;
  }> = [];
  private originalFetch: typeof fetch | null = null;
  private originalXHR: typeof XMLHttpRequest | null = null;
  private resourceObserver: PerformanceObserver | null = null;
  private seenResourceKeys = new Set<string>();
  private verboseLogging: boolean;
  private instrumentationActive = false;

  constructor(config: Partial<BandwidthMonitorConfig> = {}) {
    this.config = { ...DEFAULT_BANDWIDTH_MONITOR_CONFIG, ...config };
    this.verboseLogging = this.config.verboseLogging;
    this.sessionStartMs = performance.now();
  }

  start(): void {
    activeMonitorInstances += 1;
    if (!this.instrumentationActive) {
      this.instrumentationActive = true;
      this.patchFetch();
      this.patchXHR();
      this.observeResources();
    }
    this.exposeGlobal();
  }

  stop(): void {
    if (activeMonitorInstances <= 0) return;
    activeMonitorInstances = Math.max(0, activeMonitorInstances - 1);
    if (activeMonitorInstances > 0 || scenarioNavigationDepth > 0) {
      return;
    }
    this.teardownInstrumentation();
  }

  /** Keep instrumentation alive while a scenario navigates away from the page. */
  beginScenarioNavigation(): void {
    scenarioNavigationDepth += 1;
  }

  endScenarioNavigation(): void {
    scenarioNavigationDepth = Math.max(0, scenarioNavigationDepth - 1);
    if (activeMonitorInstances === 0 && scenarioNavigationDepth === 0) {
      this.teardownInstrumentation();
    }
  }

  private teardownInstrumentation(): void {
    if (!this.instrumentationActive) return;
    this.instrumentationActive = false;

    this.resourceObserver?.disconnect();
    this.resourceObserver = null;

    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }

    if (this.originalXHR) {
      window.XMLHttpRequest = this.originalXHR;
      this.originalXHR = null;
    }

    delete window.__benchmarkBandwidth;
  }

  reset(): void {
    this.sessionStartMs = performance.now();
    this.transfers = [];
    this.scenarioMarks = [];
    this.seenResourceKeys.clear();
    transferCounter = 0;
  }

  setVerboseLogging(enabled: boolean): void {
    this.verboseLogging = enabled;
  }

  recordTransfer(
    partial: Omit<
      NetworkTransfer,
      "id" | "durationMs" | "throughputBps" | "category"
    >,
  ): void {
    const durationMs = Math.max(1, partial.endTimeMs - partial.startTimeMs);
    const throughputBps = partial.bytes / (durationMs / 1000);
    const sanitizedUrl = sanitizeUrl(partial.url);
    const transfer: NetworkTransfer = {
      ...partial,
      id: nextTransferId(),
      durationMs,
      throughputBps,
      url: sanitizedUrl,
      category: classifyTransferUrl(sanitizedUrl),
    };

    this.transfers.push(transfer);
    if (this.transfers.length > this.config.maxTransfers) {
      this.transfers = this.transfers.slice(-this.config.maxTransfers);
    }

    if (this.verboseLogging) {
      const mbps = (throughputBps * 8) / 1_000_000;
      console.info(
        `[bandwidth] ${transfer.category} ${transfer.direction} ${transfer.method} ${transfer.url} — ${(transfer.bytes / 1024).toFixed(1)} KB @ ${mbps.toFixed(2)} Mbps`,
      );
    }
  }

  markScenario(name: string): void {
    const report = this.getReport();
    this.scenarioMarks.push({ name, atMs: performance.now(), report });
    if (this.verboseLogging) {
      console.info(
        `[bandwidth] scenario mark: "${name}"\n${formatBandwidthSummary(report)}`,
      );
    }
  }

  getScenarioMarks(): ReadonlyArray<{
    name: string;
    atMs: number;
    report: BandwidthReport;
  }> {
    return this.scenarioMarks;
  }

  getReport(): BandwidthReport {
    const conn = getConnectionInfo();
    return aggregateBandwidthReport({
      sessionStartMs: this.sessionStartMs,
      nowMs: performance.now(),
      transfers: this.transfers,
      bucketWidthMs: this.config.bucketWidthMs,
      rollingWindowMs: this.config.rollingWindowMs,
      idleThresholdBytes: this.config.idleThresholdBytes,
      connectionDownlinkMbps: conn.downlinkMbps,
      connectionRttMs: conn.rttMs,
      connectionEffectiveType: conn.effectiveType,
      includeTimeline: import.meta.env.DEV,
    });
  }

  private exposeGlobal(): void {
    const api: BandwidthMonitorGlobal = {
      getReport: () => this.getReport(),
      reset: () => this.reset(),
      setVerboseLogging: (enabled) => this.setVerboseLogging(enabled),
      markScenario: (name) => this.markScenario(name),
      getScenarioMarks: () => this.getScenarioMarks(),
    };
    window.__benchmarkBandwidth = api;
  }

  private patchFetch(): void {
    if (this.originalFetch) return;
    this.originalFetch = window.fetch.bind(window);
    const baseFetch = this.originalFetch;
    const maxBytes = this.config.maxBodyMeasureBytes;

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const startMs = performance.now();
      const method =
        init?.method ?? (input instanceof Request ? input.method : "GET");
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input.url;

      const uploadBytes = bodyByteLength(
        init?.body ?? (input instanceof Request ? input.body : null),
      );

      const response = await baseFetch(input, init);
      const endMs = performance.now();

      if (uploadBytes > 0) {
        this.recordTransfer({
          url,
          method,
          direction: "upload",
          bytes: uploadBytes,
          startTimeMs: startMs,
          endTimeMs: endMs,
          source: "fetch",
        });
      }

      const downloadBytes = await readResponseBytes(response, maxBytes);
      if (downloadBytes > 0) {
        this.recordTransfer({
          url,
          method,
          direction: "download",
          bytes: downloadBytes,
          startTimeMs: startMs,
          endTimeMs: endMs,
          source: "fetch",
        });
      }

      return response;
    };
  }

  private patchXHR(): void {
    if (this.originalXHR) return;
    const monitor = this;
    const OriginalXHR = XMLHttpRequest;
    this.originalXHR = OriginalXHR;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- patching global constructor
    (window as any).XMLHttpRequest = function PatchedXHR(this: XMLHttpRequest) {
      const xhr = new OriginalXHR();
      let method = "GET";
      let url = "";
      let uploadBytes = 0;
      let startMs = 0;

      const originalOpen = xhr.open.bind(xhr);
      xhr.open = (
        m: string,
        u: string | URL,
        async?: boolean,
        user?: string | null,
        password?: string | null,
      ) => {
        method = m;
        url = typeof u === "string" ? u : u.href;
        return originalOpen(m, u, async ?? true, user, password);
      };

      const originalSend = xhr.send.bind(xhr);
      xhr.send = (body?: Document | XMLHttpRequestBodyInit | null) => {
        uploadBytes = bodyByteLength(body as BodyInit | null | undefined);
        startMs = performance.now();

        xhr.addEventListener("loadend", () => {
          const endMs = performance.now();
          if (uploadBytes > 0) {
            monitor.recordTransfer({
              url,
              method,
              direction: "upload",
              bytes: uploadBytes,
              startTimeMs: startMs,
              endTimeMs: endMs,
              source: "xhr",
            });
          }

          const downloadBytes = xhr.responseText
            ? new TextEncoder().encode(xhr.responseText).length
            : 0;
          if (downloadBytes > 0) {
            monitor.recordTransfer({
              url,
              method,
              direction: "download",
              bytes: downloadBytes,
              startTimeMs: startMs,
              endTimeMs: endMs,
              source: "xhr",
            });
          }
        });

        return originalSend(body);
      };

      return xhr;
    } as unknown as typeof XMLHttpRequest;

    Object.assign(window.XMLHttpRequest, OriginalXHR);
    window.XMLHttpRequest.prototype = OriginalXHR.prototype;
  }

  private observeResources(): void {
    if (typeof PerformanceObserver === "undefined") return;

    try {
      this.resourceObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType !== "resource") continue;
          const resource = entry as PerformanceResourceTiming;
          const key = `${resource.name}|${resource.startTime}|${resource.transferSize}`;
          if (this.seenResourceKeys.has(key)) continue;
          this.seenResourceKeys.add(key);

          const bytes = resource.transferSize || resource.encodedBodySize;
          if (bytes <= 0) continue;

          // Ignore buffered resources that finished before this session started.
          if (resource.responseEnd < this.sessionStartMs) continue;

          const initiator = resource.initiatorType;
          if (initiator === "fetch" || initiator === "xmlhttprequest") continue;

          this.recordTransfer({
            url: resource.name,
            method: "GET",
            direction: "download" as TransferDirection,
            bytes,
            startTimeMs: resource.startTime,
            endTimeMs: resource.responseEnd,
            source: "resource",
          });
        }
      });
      this.resourceObserver.observe({ type: "resource", buffered: true });
    } catch {
      // unsupported
    }
  }
}

let singleton: NetworkBandwidthMonitor | null = null;

export const getNetworkBandwidthMonitor = (): NetworkBandwidthMonitor => {
  if (!singleton) {
    singleton = new NetworkBandwidthMonitor({
      verboseLogging: import.meta.env["VITE_BANDWIDTH_VERBOSE"] === "true",
    });
  }
  return singleton;
};
